import { DynamicTableService } from './../../../../modules/shared/services/dynamic-table/dynamic-table.service';
import { ColumnDefinition } from './../../../../modules/shared/models/dynamic-table/dynamic-table-data.model';
import { ParseHelper } from './../../../../modules/shared/parse.helper';
import { FormControlList } from './../../../../modules/shared/constants/formcontrol-list.constants';
import { AppMainService } from './../../../../app-main.service';
import { FormErrorHandler } from './../../../../modules/shared/helpers/form-error-handler.helper';
import { Fields, SectionN } from './../../../../modules/shared/models/column-settings.model';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { scrollToError } from 'src/app/modules/shared/helpers/scroll-to-error.helper';
import { WorkbookDynamicChartsM } from '../../models/workbook-dynamic-chart.models';
import { WorkspaceDynamicChartService } from '../../services/workspace-dynamic-chart.service';
import { WORKBOOK_DYNAMIC_CHARTS } from '../../constants/workbook-dynamic-charts.const';
import { WorkbookDynamicChartTypesM } from '../../models/workbook-dynamic-chart-types.models';
import { WorkbookList, WorkSpaceList } from '../../models/workspace.models';
import { WorkspaceService } from '../../services/workspace.service';

@Component({
  selector: 'app-workbook-dynamic-charts-add',
  templateUrl: './workbook-dynamic-charts-add.component.html',
  styleUrl: './workbook-dynamic-charts-add.component.scss'
})
export class WorkbookDynamicChartsAddComponent implements OnInit, OnChanges, OnDestroy {

    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    @Input() displayDialog: boolean = false;
    @Input() fromDashboard: boolean = false;
    @Input() workbookId: number = 0;
    @Input() selectedChartWidget: WorkbookDynamicChartsM = null;
    @Input() allListColumns: SectionN[] = [];
    @Output() onAddNew = new EventEmitter(null);
    @Output() onDialogClosed = new EventEmitter();

    dialogHeader: string = "Add Chart";
    workbookColumns: Fields[] = [];
    workbookStringColumns: Fields[] = [];
    workbookNumericColumns: Fields[] = [];
    chartTypes: WorkbookDynamicChartTypesM[] = ParseHelper.DeepCopy(WORKBOOK_DYNAMIC_CHARTS);
    selectedChartType: WorkbookDynamicChartTypesM = null;
    columnsForm: FormGroup; // Form group for column selection
    columnOperationsForm: FormGroup; // Form group for column operations

    // For field selection dialog
    showFieldSelectionDialog: boolean = false;
    currentEditingField: any = null;
    selectedFieldWithOperation: { field: Fields; operation: string } = { field: null, operation: null };

    operations: {name: string, code: string}[] = [
        { name: 'Sum', code: 'sum'},
        { name: 'Average', code: 'avg'},
        { name: 'Count', code: 'count'}
    ];

    detailsForm: FormGroup;
    formSubmitted: boolean = false;
    loadingFormSave: boolean = false;
    isUnsavedData: boolean = false;
    detailsFormErrors: any = {};
    formErrorHandler: FormErrorHandler = new FormErrorHandler();

    //from dashbaord
    worspaceList: WorkSpaceList[] = [];
    selectedWorkspaceId: string = null;
    workBookList: WorkbookList[] = [];
    selectedWorkbookId: string = null;

    constructor(
        private messageService: MessageService,
        private formBuilder: FormBuilder,
        private appMainService: AppMainService,
        private workspaceService: WorkspaceService,
        private dynamicTableService: DynamicTableService,
        private workspaceDynamicChartService: WorkspaceDynamicChartService
    ){}

    ngOnInit(): void {
        if(this.fromDashboard){
            this.getWorkspaceList();
        }

        this.initForm();
        this.setWorkbookColumns();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes['displayDialog'] && changes['displayDialog'].currentValue){
            this.displayDialog = changes['displayDialog'].currentValue;
        }

        if(changes['allListColumns'] && changes['allListColumns'].currentValue){
            this.allListColumns = changes['allListColumns'].currentValue;
            this.setWorkbookColumns();
        }
    }

    ngOnDestroy(): void {
        if (this.ngUnsubscribe$) {
            this.ngUnsubscribe$.next();
            this.ngUnsubscribe$.complete();
            this.ngUnsubscribe$.unsubscribe();
        }
    }

    closeDialog(){
        this.displayDialog = false;
        this.onDialogClosed.emit();
    }

    //#region load apis

    setWorkbookColumns(){
        this.workbookColumns = (this.allListColumns?.[0]?.fields||[]);

        this.workbookStringColumns = (this.allListColumns?.[0]?.fields||[]).filter(field=>
            [
                FormControlList.AutoNumber,FormControlList.UserLookup,FormControlList.Email,
                FormControlList.DropDownList,FormControlList.RadioButton,
                FormControlList.Phone,FormControlList.InputBox,FormControlList.Date,
                FormControlList.DatePicker
            ].includes(field.formControl)
        );
        this.workbookNumericColumns = (this.allListColumns?.[0]?.fields||[]).filter(field=>
            [
                FormControlList.Number,FormControlList.Decimal,FormControlList.Currency,
                FormControlList.Percentage,FormControlList.Rating
            ].includes(field.formControl)
        );
    }

    //#endregion

    //#region chart form
    initForm(){
        this.detailsFormErrors = {};
        this.isUnsavedData = false;

        this.detailsForm = this.formBuilder.group({
            id: [this.selectedChartWidget?.id || 0],
            title: [this.selectedChartWidget?.title || "", [Validators.required]],
            type: [this.chartTypes[0], [Validators.required]],
        });

        if (this.selectedChartWidget) {
            this.dialogHeader = "Edit Chart";
            const chartType = this.chartTypes.find(type => type.code === this.selectedChartWidget.type);
            this.detailsForm.get('type').setValue(chartType||null);
            this.selectedChartType = chartType;
            this.selectedWorkbookId = this.selectedChartWidget.workbookGuId;
            this.selectedWorkspaceId = this.selectedChartWidget.workSpaceId;
            this.workbookId = this.selectedChartWidget.workbookId || this.workbookId;

            if(this.fromDashboard){
                this.getWorkbookList(this.selectedWorkspaceId);
                this.getWorkbookColumns(true);
            }else{
                // Initialize column form when editing an existing chart
                setTimeout(() => this.createColumnsForm(), 0);
            }
        }else{
            this.dialogHeader = "Add Chart";
            this.onSelectChartType({value: this.chartTypes[0]});
        }

        this.formErrorHandler.handleErrors(this.detailsForm, this.detailsFormErrors, {
            title: {
                required: "Enter Chart Title"
            },
            type: {
                required: "Select Chart Type"
            }
        });

        this.detailsForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(e => this.isUnsavedData = true);
    }

    onSelectChartType($event){
        this.selectedChartType = $event.value;
        this.createColumnsForm();
    }

    createColumnsForm() {
        if (!this.selectedChartType) return;

        // Create a form group for the columns
        const columnsConfig = {};
        const columnOperationsConfig = {};

        this.selectedChartType.fields.forEach(field => {
            // Find existing columns if editing
            let initialValue: any = null;
            if (this.selectedChartWidget && this.selectedChartWidget.columns) {
                if (field.type === 'multiselect') {
                    // For multiselect, find all columns with matching isReference
                    const matchingColumns = this.selectedChartWidget.columns
                        .filter(col => col.isReference === field.name)
                        .map(col => this.workbookColumns.find(wc => wc.intId === col.columnId))
                        .filter(col => col); // Filter out undefined values

                    initialValue = matchingColumns.length ? matchingColumns : null;

                    // For each column in multiselect with showOperation true, create operation control
                    if (field.showOperation && matchingColumns.length > 0) {
                        matchingColumns.forEach((col) => {
                            const operationKey = `${field.name}_${col.intId}`;
                            // Find operation for this column
                            const columnWithOperation = this.selectedChartWidget.columns.find(
                                c => c.isReference === field.name && c.columnId === col.intId
                            );
                            columnOperationsConfig[operationKey] = [
                                columnWithOperation?.operation || null
                            ];
                        });
                    }
                } else {
                    // For dropdown, find a single column
                    const matchingColumn = this.selectedChartWidget.columns
                        .find(col => col.isReference === field.name);

                    if (matchingColumn) {
                        initialValue = this.workbookColumns.find(col => col.intId === matchingColumn.columnId) || null;

                        // If showOperation is true, create operation control for this column
                        if (field.showOperation) {
                            const operationKey = `${field.name}_${matchingColumn.columnId}`;
                            columnOperationsConfig[operationKey] = [matchingColumn?.operation || null];
                        }
                    }
                }
            }

            columnsConfig[field.name] = [initialValue, Validators.required];
        });

        this.columnsForm = this.formBuilder.group(columnsConfig);
        this.columnOperationsForm = this.formBuilder.group(columnOperationsConfig);

        // Add the columns form group to the details form
        this.detailsForm.setControl('columns', this.columnsForm);
        this.detailsForm.setControl('columnOperations', this.columnOperationsForm);
    }

    getFilteredColumns(field) {
        if (!field || !field.fieldTypes) return [];

        // Return workbook columns that match the field types specified in the chart type definition
        return this.workbookColumns.filter(column =>
            field.fieldTypes.includes(column.formControl)
        );
    }

    getAvailableColumnsForField(field) {
        if (!field || !field.fieldTypes) return [];

        // Filter out columns that are already selected for multiselect fields
        const currentlySelected = this.getSelectedColumns(field.name);
        return this.workbookColumns.filter(column =>
            field.fieldTypes.includes(column.formControl) &&
            !currentlySelected.some(selected => selected.intId === column.intId)
        );
    }

    getColumnError(fieldName: string) {
        if (!this.columnsForm) return false;
        const control = this.columnsForm.get(fieldName);
        return control?.touched && control?.invalid;
    }

    getOperationControlName(fieldName: string, column: any): string {
        return `${fieldName}_${column.intId}`;
    }

    /**
     * Get the currently selected columns for a multiselect field
     */
    getSelectedColumns(fieldName: string): any[] {
        if (!this.columnsForm) return [];

        const selectedColumns = this.columnsForm.get(fieldName).value;
        return Array.isArray(selectedColumns) ? selectedColumns : [];
    }

    /**
     * Open field selection dialog
     */
    openFieldSelectionDialog(field: any) {
        this.currentEditingField = field;
        this.selectedFieldWithOperation = { field: null, operation: null };
        this.showFieldSelectionDialog = true;
    }

    /**
     * Close field selection dialog
     */
    closeFieldSelectionDialog() {
        this.showFieldSelectionDialog = false;
        this.currentEditingField = null;
        this.selectedFieldWithOperation = { field: null, operation: null };
    }

    /**
     * Add field with operation from dialog
     */
    addFieldWithOperation() {
        if (!this.currentEditingField || !this.selectedFieldWithOperation.field) {
            this.closeFieldSelectionDialog();
            return;
        }

        const fieldName = this.currentEditingField.name;
        const selectedField = this.selectedFieldWithOperation.field;
        const selectedOperation = this.selectedFieldWithOperation.operation;
        const control = this.columnsForm.get(fieldName);

        // Get current selection (if any)
        const currentValue = control.value || [];
        const newValue = Array.isArray(currentValue) ?
            [...currentValue, selectedField] :
            [selectedField];

        // Update the form control
        control.setValue(newValue);
        control.markAsDirty();

        // Create an operation control for this new field with the selected operation
        const operationKey = this.getOperationControlName(fieldName, selectedField);
        const operationControl = new FormControl(selectedOperation);
        this.columnOperationsForm.addControl(operationKey, operationControl);

        // Close dialog
        this.closeFieldSelectionDialog();
    }

    /**
     * Remove a column from the multiselect field
     */
    removeColumnField(fieldName: string, column: any) {
        const control = this.columnsForm.get(fieldName);
        if (!control) return;

        // Filter out the column to remove
        const currentValue = control.value || [];
        if (!Array.isArray(currentValue)) return;

        const newValue = currentValue.filter(item => item.intId !== column.intId);

        // Update the form control
        control.setValue(newValue.length ? newValue : null);
        control.markAsDirty();

        // Remove the operation control for this column
        const operationKey = this.getOperationControlName(fieldName, column);
        this.columnOperationsForm.removeControl(operationKey);
    }

    /**
     * Get display name for operation code
     */
    getOperationDisplayName(operationCode: string): string {
        if (!operationCode) return 'None';

        const operation = this.operations.find(op => op.code === operationCode);
        return operation ? operation.name : 'None';
    }

    submitForm(){
        if (this.detailsForm.invalid) {
            this.formErrorHandler.showErrorOnSubmit();
            scrollToError();
            return;
        }

        const formData = this.detailsForm.getRawValue();
        const columns = [];
        const columnOperations = formData.columnOperations || {};

        // Process column selections from the form
        if (formData.columns && this.selectedChartType) {
            this.selectedChartType.fields.forEach(field => {
                const selectedColumns = formData.columns[field.name];

                if (field.type === 'multiselect' && Array.isArray(selectedColumns)) {
                    // For multiselect fields (like Y-axis), add each selected column
                    selectedColumns.forEach(column => {
                        const operationKey = `${field.name}_${column.intId}`;
                        columns.push({
                            columnId: column.intId,
                            name: column.name,
                            isReference: field.name,
                            formControl: column.formControl,
                            operation: field.showOperation ? columnOperations[operationKey] || null : null
                        });
                    });
                } else if (selectedColumns) {
                    // For single-select fields (like X-axis)
                    const operationKey = `${field.name}_${selectedColumns.intId}`;
                    columns.push({
                        columnId: selectedColumns.intId,
                        name: selectedColumns.name,
                        isReference: field.name,
                        formControl: selectedColumns.formControl,
                        operation: field.showOperation ? columnOperations[operationKey] || null : null
                    });
                }
            });
        }

        const chartDetails: WorkbookDynamicChartsM = {
            id: formData.id || 0,
            chartId: formData.id || 0,
            workbookId: this.workbookId,
            workbookGuId: this.selectedWorkbookId,
            workSpaceId: this.selectedWorkspaceId,
            title: formData.title,
            type: formData.type.code,
            operation: 'sum',
            columns: columns
        };

        this.loadingFormSave = true;

        if(this.fromDashboard){
            this.loadingFormSave = false;
            this.onAddNew.emit(chartDetails);
            this.closeDialog();
            return;
        }

        if(chartDetails.id){
            this.workspaceDynamicChartService.updateDynamicCharts(this.workbookId, chartDetails).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
                next: (res) => {
                    this.loadingFormSave = false;
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Chart updated successfully' });
                    this.onAddNew.emit(chartDetails);
                    this.closeDialog();
                },
                error: (err) => {
                    this.loadingFormSave = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.Message||'Error updating chart' });
                }
            });
        }else{
            this.workspaceDynamicChartService.addDynamicCharts(this.workbookId, chartDetails).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
                next: (res) => {
                    this.loadingFormSave = false;
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Chart added successfully' });
                    this.onAddNew.emit(chartDetails);
                    this.closeDialog();
                },
                error: (err) => {
                    this.loadingFormSave = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.Message||'Error adding chart' });
                }
            });
        }
    }
    //#endregion

    //#region workspace list
    getWorkspaceList() {
        this.appMainService.isShowSpinner('getWorkspaceList');
        this.workspaceService
            .getWorkspaceList()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (response: WorkSpaceList[]) => {
                    this.appMainService.isHideSpinner('getWorkspaceList');
                    this.worspaceList = response;
                },
                error: (error) => {
                    this.appMainService.isHideSpinner('getWorkspaceList');
                },
            });
    }

    onWorkspaceChange() {
        this.getWorkbookList(this.selectedWorkspaceId)
    }

    onWorkbookChange() {
        this.workbookId = this.workBookList.find(wb => wb.id === this.selectedWorkbookId)?.intId || 0;
        this.getWorkbookColumns();
    }

    getWorkbookList(workspaceId) {
        this.appMainService.isShowSpinner('getWorkbookList');
        this.workspaceService
            .getWorkspaceItemsDetailsByID(workspaceId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (response) => {
                    this.appMainService.isHideSpinner('getWorkbookList');
                    this.workBookList = response;
                },
                error: (error) => {
                    this.appMainService.isHideSpinner('getWorkbookList');
                },
            });
    }

    getWorkbookColumns(edit: boolean = false) {
        this.appMainService.isShowSpinner('getDynamicTableColumn');
        this.dynamicTableService.getDynamicTableColumn(this.selectedWorkbookId).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
            next: (response: ColumnDefinition[]) => {
                this.appMainService.isHideSpinner('getDynamicTableColumn');
                this.allListColumns = this.mapWorkbookColumnDefinitions(response||[]);
                this.setWorkbookColumns();

                if(edit) {
                    this.createColumnsForm();
                }
            },
            error: (error) => {
                this.appMainService.isHideSpinner('getDynamicTableColumn');
            }
        })
    }

    mapWorkbookColumnDefinitions(columnDefinitions: ColumnDefinition[]): any[] {
        return [
            {
            customFields: [],
            fields: columnDefinitions
            .sort((a, b) => (a.displayOrder) - (b.displayOrder))
            .map((col) => ({
                id: col.id,
                intId: col.intId,
                name: col.name,
                labelName: col.name,
                field: col.name,
                columnName: col.name,
                dragable: true,
                operators: col.searchOperators,
                formControlId: col.formControlId,
                formControl: col.formControlName,
                fieldDataType: col.dataType,
                enabledInForm: true,
                enabled: col.enable,
                isDateWithTime: false,
                displayOrder: col?.displayOrder || col?.order,
                readOnly: false,
                childFields: [],
                ischecked: true,
                custom: true,
                isRequired : false,
                isDisabled : false,

            })),
            id: 1,
            name: 'Workbook columns',
            systemGenerated: true,
            },
        ];
    }
    //#endregion
}
